/**
 * One store for assistant conversations, mirrored between this browser and the server.
 *
 * The server is the source of truth; localStorage is a synchronous local cache in front of
 * it. That split exists because every caller here is synchronous - the sidebar renders from
 * loadConversations() during a render pass - so the cache answers immediately and the
 * network catches up in the background.
 *
 * Server-side, each conversation is sealed with a key belonging to its owner alone (see
 * server/services/contentCrypto.js), so history now survives a cleared cache, follows the
 * lawyer to a second device, and is unreadable to anyone who reaches the database.
 *
 * Two surfaces read this list: the sidebar's chat history and the full chat page. Each used
 * to keep its own copy, so sending a message on the chat page left the always-mounted
 * sidebar showing a stale list until it happened to remount. Writes go through here and
 * fire a same-tab change event, so every subscriber refreshes.
 *
 * The keys are the original ones — conversations from earlier versions still load.
 */

const CONVERSATIONS_BASE = 'flormar-chat-conversations';
const ACTIVE_ID_BASE = 'flormar-chat-active-id';
export const EXPAND_STATE_KEY = 'flormar-chat-expand-messages';
const CHANGE_EVENT = 'bagent-chat-store-change';

/**
 * WHOSE conversations these are.
 *
 * They used to live under one unscoped key, so every account sharing a browser shared a
 * history: one lawyer signed out, the next signed in, and the sidebar showed them the first
 * one's conversations - client names, case facts, the questions you only ask about a matter
 * you are acting on. Privileged material crossing between users of the same machine.
 *
 * Derived here rather than passed in because this module is imported by plain functions
 * outside React, and an owner that has to be threaded through every call site is an owner
 * somebody eventually forgets to pass.
 *
 * Returns '' when nobody is signed in, and the store then reads and writes NOTHING - a
 * signed-out page has no business showing a history, and writing under a blank owner would
 * recreate the shared bucket this fixes.
 */
function currentOwner() {
  try {
    const local = localStorage.getItem('bagent_local_user');
    if (local) {
      const u = JSON.parse(local);
      if (u?.email) return u.email.toLowerCase();
    }
  } catch { /* fall through */ }
  try {
    const entra = localStorage.getItem('entraIdToken');
    if (entra) {
      const part = entra.split('.')[1];
      const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
      const email = (claims.preferred_username || claims.upn || claims.email || '').toLowerCase();
      if (email) return email;
    }
  } catch { /* not signed in, or an unreadable token */ }
  return '';
}

function conversationsKey() {
  const owner = currentOwner();
  return owner ? `${CONVERSATIONS_BASE}::${owner}` : '';
}

function activeIdKey() {
  const owner = currentOwner();
  return owner ? `${ACTIVE_ID_BASE}::${owner}` : '';
}

/**
 * Remove the old unscoped blob.
 *
 * DELETED, not migrated to whoever happens to be signed in now. Migrating would hand one
 * person's client conversations to another on exactly the shared machine this is meant to
 * protect, and there is nothing in the data saying who wrote it. Losing a local history is
 * a nuisance; attributing privileged material to the wrong lawyer is not.
 */
function dropUnscopedHistory() {
  try {
    if (localStorage.getItem(CONVERSATIONS_BASE) !== null) {
      localStorage.removeItem(CONVERSATIONS_BASE);
      localStorage.removeItem(ACTIVE_ID_BASE);
    }
  } catch { /* ignore */ }
}
dropUnscopedHistory();

/**
 * Forget the signed-in user's conversations. Called on explicit sign-out: choosing to leave
 * a shared machine should not leave privileged material in its localStorage for the next
 * person with devtools. An idle timeout deliberately does NOT call this - the same person is
 * coming back, and losing their work would be its own kind of failure.
 */
export function clearConversationsForCurrentUser() {
  try {
    const ck = conversationsKey();
    const ak = activeIdKey();
    if (ck) localStorage.removeItem(ck);
    if (ak) localStorage.removeItem(ak);
    notify();
  } catch { /* ignore */ }
}

export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function titleFromMessages(msgs) {
  const first = (msgs || []).find((m) => m.role === 'user');
  if (!first) return '';
  const text = first.content || '';
  return text.length > 50 ? text.slice(0, 50) + '…' : text;
}

function notify() {
  // A `storage` event only reaches *other* tabs, so the same-tab refresh needs its own.
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch { /* non-browser environment (tests) */ }
}

export function loadConversations() {
  try {
    const key = conversationsKey();
    if (!key) return [];
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Persist conversations, surviving a full localStorage quota.
 *
 * This used to be a bare try/catch that swallowed the error. When the ~5 MB quota was
 * reached the write failed COMPLETELY and silently: the in-memory array still held the new
 * content so the UI looked right, but the next mount reloaded the last state that actually
 * saved, and the newest work simply vanished. That is the "it cuts in half when I switch
 * tabs" report, and generated documents growing from ~3 to ~44 pages made it routine.
 *
 * Now the oldest conversations are evicted until the write fits. Losing the oldest thread
 * is recoverable and visible; losing the newest silently is neither. The caller is told
 * how many were dropped so it can say so instead of pretending nothing happened.
 *
 * @returns {{ok: boolean, evicted: number}}
 */
export function saveConversations(convos) {
  let working = convos;
  let evicted = 0;
  for (let attempt = 0; attempt < 40; attempt += 1) {
    try {
      const key = conversationsKey();
      if (!key) return;
      localStorage.setItem(key, JSON.stringify(working));
      // Local write first, network second. A failed or slow request must never be able to
      // lose what the user just typed - the cache already holds it, and the push retries on
      // the next save.
      schedulePush(working);
      notify();
      return { ok: true, evicted };
    } catch (e) {
      // Only a quota failure is worth retrying; anything else (disabled storage, private
      // mode) will not improve by dropping data.
      const quota = e && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || e.code === 22);
      if (!quota || working.length <= 1) break;
      // Evict the least recently updated, never the active/newest one.
      const oldest = working.reduce((min, c, i, arr) => (
        (c.updatedAt || 0) < (arr[min].updatedAt || 0) ? i : min), 0);
      working = working.filter((_, i) => i !== oldest);
      evicted += 1;
    }
  }
  notify();
  return { ok: false, evicted };
}

export function loadActiveId() {
  try {
    const key = activeIdKey();
    return key ? localStorage.getItem(key) || null : null;
  } catch {
    return null;
  }
}

export function saveActiveId(id) {
  try {
    const key = activeIdKey();
    if (!key) return;
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch { /* ignore */ }
  notify();
}

/** Conversations that have actually been used, newest first. */
export function listConversations() {
  return loadConversations()
    .filter((c) => (c.messages || []).length > 0)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

/** Subscribe to any write made through this module (same tab) or another tab. */
export function subscribe(listener) {
  const onChange = () => listener();
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/**
 * Create a conversation and persist it, newest-first. Returns its id.
 *
 * This is how a generated document becomes a real thread rather than a parallel list:
 * the Doküman Üretici writes the instruction as the user turn and the finished draft as an
 * assistant turn carrying a `document` card, so the sidebar and the chat page render it with
 * the same components as anything else. Before this, drafts lived in their own
 * 'bagent-drafting-history' key and never appeared in Sohbetler at all.
 */
export function appendConversation({ title, messages, updatedAt, meta } = {}) {
  const id = generateId();
  const convo = {
    id,
    title: title || titleFromMessages(messages) || '',
    messages: Array.isArray(messages) ? messages : [],
    updatedAt: updatedAt || Date.now(),
    // `meta` was accepted by callers but not by this function, so DraftingStudio has been
    // passing a meta object that was silently discarded. Kept now, so an entry can record
    // what produced it (analysis / comparison / draft) and which document it belongs to.
    ...(meta && typeof meta === 'object' ? { meta } : {}),
  };
  saveConversations([convo, ...loadConversations()]);
  return id;
}

/**
 * Fold the old drafting-history key into conversations, once.
 *
 * Entries were { title, document, meta }. They become a two-message thread so they open,
 * render and export like any other conversation. The key is removed afterwards so this
 * cannot run twice and duplicate them.
 *
 * @param {(entry:object)=>object[]} toMessages builds the message pair for one entry
 * @returns {number} how many were migrated
 */
export function migrateLegacyDrafts(legacyKey, toMessages) {
  let legacy;
  try {
    legacy = JSON.parse(localStorage.getItem(legacyKey) || '[]');
  } catch {
    legacy = [];
  }
  if (!Array.isArray(legacy) || legacy.length === 0) return 0;

  const existing = loadConversations();
  // Oldest first so the newest legacy draft ends up nearest the top of the list.
  const migrated = [...legacy].reverse().map((entry) => ({
    id: generateId(),
    title: entry.title || '',
    messages: toMessages(entry) || [],
    updatedAt: entry.ts ? new Date(entry.ts).getTime() : Date.now(),
  })).filter((c) => c.messages.length > 0);

  if (migrated.length === 0) {
    try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
    return 0;
  }
  saveConversations([...migrated.reverse(), ...existing]);
  try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
  return migrated.length;
}


// ═══════════════════════════════════════════════════════════════════════════════
// Server sync
// ═══════════════════════════════════════════════════════════════════════════════
//
// What crosses the wire is the conversation as it appears here; what is STORED is an
// AES-256-GCM envelope sealed with the signed-in user's own data key. The server derives the
// owner from the session token, never from anything sent here, so there is no field in these
// requests that could name a different user's history.

const API_BASE = process.env.REACT_APP_API_URL || '';

/** The bearer token for the current session, or '' when signed out. */
function sessionToken() {
  try {
    return localStorage.getItem('entraIdToken') || localStorage.getItem('bagent_local_session') || '';
  } catch {
    return '';
  }
}

async function api(path, init = {}) {
  const token = sessionToken();
  if (!token) throw new Error('signed out');
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers || {}) },
  });
  if (!res.ok) throw new Error(`${init.method || 'GET'} ${path} -> ${res.status}`);
  return res.status === 204 ? null : res.json();
}

// id -> the updatedAt we last successfully pushed, so a save only sends what changed.
let pushedAt = new Map();
let pushTimer = null;
let pushQueue = null;

function schedulePush(convos) {
  if (!sessionToken()) return;
  pushQueue = convos;
  if (pushTimer) return;
  // Debounced: typing a message rewrites the whole list on every keystroke-driven save, and
  // one PUT per keystroke would be both wasteful and a rate-limit problem.
  pushTimer = setTimeout(() => { pushTimer = null; flushPush().catch(() => {}); }, 1500);
}

async function flushPush() {
  const convos = pushQueue;
  pushQueue = null;
  if (!Array.isArray(convos) || !sessionToken()) return;
  for (const c of convos) {
    if (!c?.id || !(c.messages || []).length) continue;
    if (pushedAt.get(c.id) === c.updatedAt) continue;
    try {
      await api(`/chat-history/${encodeURIComponent(c.id)}`, {
        method: 'PUT',
        body: JSON.stringify({ title: c.title || '', messages: c.messages || [], updatedAt: c.updatedAt, meta: c.meta }),
      });
      pushedAt.set(c.id, c.updatedAt);
    } catch {
      // Left un-marked, so the next save retries it. Silent on purpose: a lawyer mid-sentence
      // does not need a toast about a background sync, and the local copy is intact.
      break;
    }
  }
}

/**
 * Merge two lists of conversations by id, newest write winning.
 *
 * Last-write-wins on updatedAt rather than a real merge: two devices editing the SAME
 * conversation at the same second is vanishingly rare, and the alternative - interleaving
 * message arrays - can invent a transcript that neither side ever saw. In a legal record,
 * a missing edit is recoverable and a fabricated one is not.
 */
function mergeConversations(local, remote) {
  const byId = new Map();
  for (const c of local) if (c?.id) byId.set(c.id, c);
  for (const r of remote) {
    if (!r?.id) continue;
    const mine = byId.get(r.id);
    if (!mine || (r.updatedAt || 0) > (mine.updatedAt || 0)) byId.set(r.id, r);
  }
  return [...byId.values()].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

let pulling = null;
let pulledFor = '';

/**
 * Bring this browser up to date with the server.
 *
 * Runs once per signed-in owner per page load. Safe to call repeatedly - a second call
 * while one is in flight joins it rather than starting another.
 */
export async function pullFromServer({ force = false } = {}) {
  const owner = currentOwner();
  if (!owner || !sessionToken()) return [];
  if (!force && pulledFor === owner) return loadConversations();
  if (pulling) return pulling;

  pulling = (async () => {
    try {
      const data = await api('/chat-history?limit=300');
      const remote = (data?.conversations || []).map((c) => ({
        id: c.id,
        title: c.title || '',
        messages: Array.isArray(c.messages) ? c.messages : [],
        // The server keeps ISO timestamps; this store has always compared numbers.
        updatedAt: typeof c.updatedAt === 'number' ? c.updatedAt : Date.parse(c.updatedAt) || 0,
        ...(c.meta ? { meta: c.meta } : {}),
      }));
      const merged = mergeConversations(loadConversations(), remote);
      // Remember what the server already has, so this pull does not immediately push it all
      // straight back.
      for (const c of remote) pushedAt.set(c.id, c.updatedAt);
      const key = conversationsKey();
      if (key) {
        try { localStorage.setItem(key, JSON.stringify(merged)); } catch { /* quota - the cache is optional */ }
      }
      pulledFor = owner;
      notify();
      return merged;
    } catch {
      // Offline, or the endpoint is not deployed yet. The local cache keeps working exactly
      // as it did before this sync existed.
      return loadConversations();
    } finally {
      pulling = null;
    }
  })();
  return pulling;
}

/** Call once the user is known. Idempotent. */
export function initChatHistorySync() {
  pullFromServer().catch(() => {});
}

/**
 * Delete a conversation for good - here AND on the server.
 *
 * Separate from clearConversationsForCurrentUser(), which only wipes this browser's cache on
 * sign-out. Signing out of a shared machine must not delete your history from your account.
 */
export async function deleteConversation(id) {
  const remaining = loadConversations().filter((c) => c.id !== id);
  const key = conversationsKey();
  if (key) {
    try { localStorage.setItem(key, JSON.stringify(remaining)); } catch { /* ignore */ }
  }
  pushedAt.delete(id);
  notify();
  try {
    await api(`/chat-history/${encodeURIComponent(id)}`, { method: 'DELETE' });
  } catch { /* already gone, or offline; the local copy is removed either way */ }
  return remaining;
}

/** Forget sync state when the signed-in user changes, so nothing carries across accounts. */
export function resetSyncState() {
  pushedAt = new Map();
  pulledFor = '';
  pushQueue = null;
  if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
}
